import React, { FC, useState } from 'react';
import moment from 'moment';

import { DarkBlueHeaderL } from '../../../../Themes';
import ActionButton from '../../../../components/UI/PaginatedTable/TableActions/ActionButton';
import Tooltip from '../../../../components/UI/Tooltip';
import { isAfter } from '../../../../lib/TimeUtils';

import { cogs } from '../../../../assets/icons';
import { Company, ParticipantListView, CompanyEventStatus } from '../../../../lib/Types';

import { Wrapper, Container, BrightBlueHeader, InnerCol, GenTeamsText } from './Styles';
import { theme } from '../../../../theme';

export interface TeamGenerationWidgetProps {
  participantsWithoutTeams: ParticipantListView[];
  companyOptions?: Company[];
  companyId?: number;
  selectedCompany?: number;
  generateTeam: (companyId: number, data: any) => any;
  handleSelect?: (val: number) => void;
  event?: CompanyEventStatus;
}

const TeamGenerationWidget: FC<TeamGenerationWidgetProps> = ({
  participantsWithoutTeams,
  companyId,
  generateTeam,
  selectedCompany,
  event
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const totalParticipants = selectedCompany
    ? participantsWithoutTeams.filter(participant => participant.companyId === selectedCompany)
        .length
    : participantsWithoutTeams.length;

  // Check if event is active and not completed
  const isEventActive = () => {
    if (!event) return false;
    
    const now = moment().valueOf();
    const eventStartDate = parseInt(event.startDate);
    const eventEndDate = parseInt(event.endDate);
    const graceEndDate = eventEndDate + (86400 * 1000 * event.gracePeriod); // Grace period in milliseconds
    
    // Event is active if current time is after event start and before grace period ends
    return now >= eventStartDate && now < graceEndDate;
  };

  const handleSubmit = () => {
    if (companyId) generateTeam(companyId, {});
    else {
      if (!selectedCompany) return;
      else generateTeam(selectedCompany, {});
    }
  };

  const isButtonEnabled = isEventActive();
  
  const getDisabledMessage = () => {
    if (!event) return "No event data available";
    
    const now = moment().valueOf();
    const eventStartDate = parseInt(event.startDate);
    const eventEndDate = parseInt(event.endDate);
    const graceEndDate = eventEndDate + (86400 * 1000 * event.gracePeriod);
    
    if (now < eventStartDate) {
      return "Event has not started yet";
    } else if (now >= graceEndDate) {
      return "Event has completed";
    }
    
    return "Event is not active";
  };

  return (
    <Wrapper>
      <Container>
        <InnerCol>
          <BrightBlueHeader>Participants without teams</BrightBlueHeader>
          <DarkBlueHeaderL fontStyle={'inherit'}>{totalParticipants}</DarkBlueHeaderL>
        </InnerCol>
        <InnerCol>
          <GenTeamsText textAlign="center">Allocate these users to new teams</GenTeamsText>
          <div 
            style={{ position: 'relative', display: 'inline-block' }}
            onMouseEnter={() => !isButtonEnabled && setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <ActionButton
              onClick={handleSubmit}
              icon={cogs}
              backgroundColour={theme.primary.paleBlue}
              darkText
              disabled={!isButtonEnabled}
            >
              Generate teams
            </ActionButton>
            {!isButtonEnabled && showTooltip && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#333',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  zIndex: 1000,
                  marginBottom: '8px',
                  pointerEvents: 'none'
                }}
              >
                {getDisabledMessage()}
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    border: '4px solid transparent',
                    borderTopColor: '#333'
                  }}
                />
              </div>
            )}
          </div>
        </InnerCol>
      </Container>
    </Wrapper>
  );
};

export default TeamGenerationWidget;
